/* eslint-disable no-unused-vars */
import React, { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getCookie } from 'helper/cookie';
import { Row, Col, Form, Typography } from 'antd';
import { get } from 'lodash';
import { getCustomerOwnerInReportGoverment } from 'api/customer';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableFooter,
  Typography as TypographyMui,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { NumberFormat } from 'helper/currency';

const { Title } = Typography;

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: '#ede7f6',
  color: '#4a148c',
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '14px',
  borderBottom: '2px solid #b39ddb',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#f3e5f5',
  },
  '&:hover': {
    backgroundColor: '#e1bee7',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #7b1fa2 0%, #ab47bc 100%)',
  border: 0,
  borderRadius: 30,
  boxShadow: '0 4px 8px rgba(171, 71, 188, 0.4)',
  color: 'white',
  height: 48,
  padding: '0 30px',
  fontWeight: 'bold',
  '&:hover': {
    background: 'linear-gradient(135deg, #ab47bc 0%, #7b1fa2 100%)',
  },
}));

const CardContainer = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(4),
  boxShadow: theme.shadows[6],
  borderRadius: 20,
  padding: theme.spacing(3),
  backgroundColor: '#fdfdfd',
}));

const CustomerReportComponents = () => {
  const isMobileView = useSelector((state: any) => get(state, 'device.isMobileView', false));
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [paginate, setPaginate] = useState<any>({ limit: 9999, offset: 0 });
  const token = getCookie('access_tokenltl');
  const [form] = Form.useForm();

  const fetchData = useCallback(
    async (query?: any) => {
      setIsLoading(true);
      const params = {
        limit: query?.limit || paginate.limit,
        offset: query?.offset || paginate.offset,
        order_by: 'id',
        order_type: 'DESC',
      };
      try {
        const responseData = await getCustomerOwnerInReportGoverment({ token, params });
        if (responseData?.status === 200) {
          setData(responseData.data?.data);
        } else {
          setData([]);
        }
      } catch (error) {
        setData([]);
      } finally {
        setIsLoading(false);
      }
    },
    [paginate.limit, token],
  );

  useEffect(() => {
    fetchData();
  }, [token, fetchData]);

  const DataTable = () => {
    const totalPreviousPay = data.reduce((acc: number, ownerData: any) => acc + (ownerData.previousPeriodPay?.thrust_data[0]?.totalPay || 0), 0);
    const totalLatestPay = data.reduce((acc: number, ownerData: any) => acc + (ownerData.latestPeriodPay?.thrust_data[0]?.totalPay || 0), 0);

    return (
      <CardContainer>
        <TypographyMui variant="h6" gutterBottom align="center">
          รายงานการใช้งาน Owner รัฐบาล
        </TypographyMui>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>ID</StyledTableCell>
                <StyledTableCell>ชื่อ Owner</StyledTableCell>
                <StyledTableCell>Username</StyledTableCell>
                <StyledTableCell>ยอดรวม งวดก่อนหน้า</StyledTableCell>
                <StyledTableCell>ยอดรวม งวดปัจจุบัน</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((ownerData: any) => (
                <StyledTableRow key={ownerData.owner.id}>
                  <TableCell align="center">{ownerData.owner.id}</TableCell>
                  <TableCell align="center">{`${ownerData.owner.name} ${ownerData.owner.lastname}`}</TableCell>
                  <TableCell align="center">{ownerData.owner.username}</TableCell>
                  <TableCell align="right">{NumberFormat(ownerData.previousPeriodPay?.thrust_data[0]?.totalPay || 0)}</TableCell>
                  <TableCell align="right">{NumberFormat(ownerData.latestPeriodPay?.thrust_data[0]?.totalPay || 0)}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <StyledTableCell colSpan={3} align="right">
                  ยอดรวมทั้งหมด
                </StyledTableCell>
                <StyledTableCell align="right">{NumberFormat(totalPreviousPay)}</StyledTableCell>
                <StyledTableCell align="right">{NumberFormat(totalLatestPay)}</StyledTableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </CardContainer>
    );
  };

  return (
    <Form autoComplete="off" form={form} style={{ padding: 24 }}>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 24 }}>
        📊 รายงานยอดใช้งาน Owner รัฐบาล
      </Title>
      <Row justify="center">
        <Col span={24} className="table-responsive">
          <DataTable />
        </Col>
      </Row>
    </Form>
  );
};

export default CustomerReportComponents;
